import React, { useEffect, useMemo, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import SectionCard from '@/components/SectionCard';
import { ENV, useAppConfig } from '@/modules/config';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { ContactLogic } from '@/modules/contact';
import { newPreview } from '@/modules/topic/preview';
import { TemplateLogic, type MessageIntent } from '@/modules/template';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import ShareWizardHeader from './ShareWizardHeader';
import RecipientBox from './RecipientBox';
import { ShareWizardLogic } from './logic';
import { Destinations } from './navigation_logic';
import MessagePreviewSheet from './MessagePreviewSheet';
import { useSharingInterstitialsWithWizardTracker } from './useSharingInterstitials';
import {
  FieldEditor,
  Label,
  renderPreviewMessage,
  SMSLinkPreviewCard,
} from './compose';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import ShareContentSkeleton, {
  SHARE_STEP_HELP_TEXT,
} from './ShareContentSkeleton';

interface ComposeWithTemplateProps {
  intent: MessageIntent;
}

const ComposeWithTemplate: React.FC<ComposeWithTemplateProps> = ({
  intent,
}) => {
  const {
    topic,
    isTopicLoading,
    topicError,
    inProgressLegId,
    sharingMode,
    reshareInfoForPrevLeg,
    getTemplateForIntent,
    fieldValues,
    getContextForIntent,
    getContextForContact,
    getPreviewForIntent,
    getContactsForIntent,
    setFieldValue,
    effectiveSingletonMode,
    intentReachesOnePerson,
    maxRecipientsPerLeg,
    isTemplateHydrated,
    handleComposeNext,
    nav,
  } = useShareWizardContext();
  const messageDestination = Destinations.message(intent);

  const [showPreviewSheet, setShowPreviewSheet] = useState(false);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const template = getTemplateForIntent(intent);
  const context = getContextForIntent(intent);
  const previewMessage = getPreviewForIntent(intent);
  const intentContacts = getContactsForIntent(intent);

  // Whether each message for THIS intent reaches exactly one person — the
  // wizard's own reading, so the preview, the field walk and completeness
  // cannot drift apart. Not the same question as effectiveSingletonMode, which
  // is about the whole wizard and is what the two lines below want.
  const reachesOnePerson = intentReachesOnePerson(intent);

  // When every message reaches one person but there are multiple recipients,
  // each text is sent individually — reflect that in the preview.
  const isSeriesOfSingletonTexts =
    effectiveSingletonMode && intentContacts.length > 1;
  const { features } = useAppConfig();
  const isReshare = sharingMode === 'reshare';
  const showPreviewSheetButton = topicGardenEnabled(features);
  const titleLabel = ShareWizardLogic.composeStepTitle(
    intent,
    intentContacts.length,
    effectiveSingletonMode,
    maxRecipientsPerLeg
  );
  // A reshare's message reads on from the leg it passes, so it cannot be shown
  // or sent until that leg's details come back from the server.
  const reshareStoryReady = !isReshare || !!reshareInfoForPrevLeg;
  const previewLabel = isSeriesOfSingletonTexts
    ? 'Preview (click contact for specific peek)'
    : 'Preview';

  // Fields that offer nothing here are dropped from the preview, so they must
  // not be steps in the walk either — the user can never reach one.
  const orderedFields = useMemo(
    () =>
      template
        ? TemplateLogic.getUserFieldsInBodyOrder(template).filter(
            field =>
              !TemplateLogic.isVestigialField(field, context, reachesOnePerson)
          )
        : [],
    [template, context, reachesOnePerson]
  );

  const sortByLastName = useMemo(
    () => ContactLogic.lastNameComparator(intentContacts),
    [intentContacts]
  );
  const [activeFieldId, setActiveFieldId] = useState<string | null>(
    orderedFields[0]?.id ?? null
  );

  // In singleton mode the message personalizes per recipient, so let the user
  // click a recipient chip to preview that person's name substitution. Defaults
  // to the first chip (sorted) so the highlighted chip matches the preview.
  const [previewContactId, setPreviewContactId] = useState<string | null>(null);
  const sortedContactIds = useMemo(
    () => intentContacts.map(c => c.localId).sort(sortByLastName),
    [intentContacts, sortByLastName]
  );
  const previewContact = effectiveSingletonMode
    ? (intentContacts.find(c => c.localId === previewContactId) ??
      intentContacts.find(c => c.localId === sortedContactIds[0]) ??
      null)
    : null;
  const previewContext = previewContact
    ? getContextForContact(previewContact)
    : context;

  useEffect(() => {
    if (!template) {
      setActiveFieldId(null);
      return;
    }

    setActiveFieldId(current =>
      current && orderedFields.some(field => field.id === current)
        ? current
        : (orderedFields[0]?.id ?? null)
    );
  }, [template, orderedFields]);

  const canSend =
    !!template &&
    isTemplateHydrated(intent) &&
    reshareStoryReady &&
    inProgressLegId == null;
  const isFinalComposeStep = nav.isLastMessage(messageDestination);

  const { beforeSharing, phoneLinkingShown, interstitials } =
    useSharingInterstitialsWithWizardTracker(isFinalComposeStep);

  const activeField =
    template?.fields.find(field => field.id === activeFieldId) ?? null;
  const smsPreview = useMemo(
    () =>
      newPreview(
        topic,
        window.location.origin,
        ENV.apiBaseUrl || window.location.origin
      ),
    [topic]
  );
  const advanceToNextMissingField = (
    currentFieldId: string,
    nextFieldValues: Readonly<Record<string, string>>
  ) => {
    if (!template) return;
    const missingFieldIds = TemplateLogic.getMissingFieldIds(
      template,
      nextFieldValues,
      context,
      reachesOnePerson
    );
    const currentIndex = orderedFields.findIndex(
      field => field.id === currentFieldId
    );
    const nextMissingField = orderedFields
      .slice(currentIndex + 1)
      .find(field => missingFieldIds.includes(field.id));
    const firstMissingField = orderedFields.find(field =>
      missingFieldIds.includes(field.id)
    );

    setActiveFieldId(
      nextMissingField?.id ?? firstMissingField?.id ?? currentFieldId
    );
  };
  const advanceToNextField = (currentFieldId: string) => {
    if (!template) return;
    const currentIndex = orderedFields.findIndex(
      field => field.id === currentFieldId
    );
    const nextField = orderedFields[currentIndex + 1];

    setActiveFieldId(nextField?.id ?? currentFieldId);
  };

  const showCompose = !!(template && topic && !isTopicLoading && !topicError);

  return (
    <IonPage>
      <ShareWizardHeader
        topicTitle={topic?.title ?? ''}
        title={titleLabel}
        progress={nav.progress}
        onBack={nav.goBack}
      />
      <ActionGuardContainer>
        <ActionGuard
          disableMode="hidden"
          optimistic
          action={ACTIONS.SHARE}
          resource={RESOURCES.TOPICS}
          resourceId={topic?.id}
          helpText={SHARE_STEP_HELP_TEXT}
          fallbackElem={lock => <ShareContentSkeleton lock={lock} />}
          guardedElem={
            <>
              <IonContent
                scrollY={false}
                style={{ '--background': 'var(--app-page-background)' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '4px 20px 0',
                    boxSizing: 'border-box',
                  }}
                >
                  {isTopicLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <IonSpinner />
                    </div>
                  ) : topicError ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <p style={{ color: '#c33' }}>{topicError}</p>
                      <IonButton onClick={nav.exit} fill="clear">
                        Close
                      </IonButton>
                    </div>
                  ) : template && topic ? (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <RecipientBox
                          to={null}
                          value={intentContacts.map(contact => contact.localId)}
                          sort={sortByLastName}
                          readOnly
                          collapsedRows={3}
                          expandable={false}
                          onSelectContact={
                            effectiveSingletonMode
                              ? setPreviewContactId
                              : undefined
                          }
                        />
                      </div>
                      <Label text={previewLabel} />
                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          overflow: 'auto',
                          paddingBottom: '16px',
                        }}
                      >
                        <div>
                          <SectionCard
                            className="section-card--message-preview"
                            noBottomMargin
                          >
                            <div
                              className="section-card__message-preview"
                              data-testid="share-message-preview"
                            >
                              {/* A reshare's story primitives render empty
                                  until the parent leg's info is retrieved, so
                                  we should wait for that. */}
                              {!reshareStoryReady ? (
                                <IonSpinner />
                              ) : previewMessage ? (
                                renderPreviewMessage(
                                  template,
                                  fieldValues,
                                  previewContext,
                                  activeFieldId,
                                  setActiveFieldId,
                                  reachesOnePerson
                                )
                              ) : (
                                'Preview will appear here...'
                              )}
                            </div>
                          </SectionCard>
                        </div>
                        {/* The link card previews how the topic unfurls in the
                            recipient's SMS app. A reshare's own message already
                            carries the story it is passing on, and its sender
                            has seen that unfurl — it is how they got here. */}
                        {!isReshare && (
                          <SectionCard
                            className="section-card--sms-link-preview"
                            noBottomMargin
                          >
                            <SMSLinkPreviewCard preview={smsPreview} />
                          </SectionCard>
                        )}
                        <div style={{ flex: 1, minHeight: '16px' }} />
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <p style={{ color: '#888' }}>
                        Pick a tone first to start composing.
                      </p>
                    </div>
                  )}
                </div>
              </IonContent>
              {showCompose && (
                <FooterDock background="opaque">
                  {/* The field editor rides the keyboard-pinned row, so it rises above
              the on-screen keyboard while the user is typing/picking a value and
              otherwise rests one gap atop the Start sharing row. */}
                  {activeField && (
                    // Drop the row's resting bottom clearance: it floats one gap above
                    // the Start sharing row, which already carries the safe-area padding,
                    // so the default dock clearance here is just dead whitespace.
                    <FooterDockRow
                      pin="keyboard"
                      contentStyle={{ paddingBottom: 0 }}
                    >
                      <div data-testid="share-field-editor">
                        <FieldEditor
                          key={activeField.id}
                          field={activeField}
                          value={fieldValues[activeField.id] ?? ''}
                          context={previewContext}
                          singletonMode={reachesOnePerson}
                          disabled={inProgressLegId != null}
                          onValueChange={v => setFieldValue(activeField.id, v)}
                          onValueCommit={v => {
                            const nextFieldValues = {
                              ...fieldValues,
                              [activeField.id]: v,
                            };
                            advanceToNextMissingField(
                              activeField.id,
                              nextFieldValues
                            );
                          }}
                          onOtherInputSubmit={v => {
                            setFieldValue(activeField.id, v);
                            advanceToNextField(activeField.id);
                          }}
                        />
                      </div>
                    </FooterDockRow>
                  )}
                  {/* What this sets in motion: the same message as the next
                      person would pass it on, addressed to someone the sender
                      has never met. The compose step above only shows their own
                      end of it. */}
                  {showPreviewSheetButton && (
                    <FooterDockRow>
                      <SmartCaseButton
                        expand="block"
                        fill="outline"
                        data-testid="share-wizard-preview-sheet"
                        onClick={() => setShowPreviewSheet(true)}
                        style={{ margin: 0 }}
                      >
                        Preview hypothetical reshare
                      </SmartCaseButton>
                    </FooterDockRow>
                  )}
                  {/* The primary topic uses the default bottom pinning — it stays docked
              to the screen bottom (the field-editor row floats above it). */}
                  <FooterDockRow landmark>
                    <SmartCaseButton
                      expand="block"
                      color="primary"
                      data-testid="share-wizard-send"
                      disabled={!canSend}
                      onClick={() =>
                        isFinalComposeStep
                          ? beforeSharing(() => handleComposeNext(intent))
                          : handleComposeNext(intent)
                      }
                      style={{ margin: 0 }}
                    >
                      {isFinalComposeStep
                        ? phoneLinkingShown
                          ? 'Start sharing for realz'
                          : 'Start sharing'
                        : 'Next'}
                    </SmartCaseButton>
                  </FooterDockRow>
                </FooterDock>
              )}
            </>
          }
        />
      </ActionGuardContainer>

      {interstitials}

      <MessagePreviewSheet
        isOpen={showPreviewSheet}
        intent={intent}
        onDismiss={() => setShowPreviewSheet(false)}
      />
    </IonPage>
  );
};

export default ComposeWithTemplate;
